import base64 from 'react-native-base64';
import { firebase } from '../config/configureFirebase';
import { get, runTransaction } from "firebase/database";
import { isValidCPF } from './Validator';
import AccessKey from './AccessKey';
import store from '../store/store';

// ---------------------------------------------------------------------------
// Definição estática de cada API
// ---------------------------------------------------------------------------
export const INTEGRATION_DEFINITIONS = {
  cpf: {
    key: 'cpf',
    requiresToken: true,
    tokenHeader: 'X-API-Key',
    hasLocalFallback: true,
    fallbackDescription: 'Usa validador local de CPF',
  },
  plates: {
    key: 'plates',
    requiresToken: true,
    tokenInUrl: true,
    hasLocalFallback: false,
    fallbackDescription: 'Consulta de placas indisponível',
  },
  cep: {
    key: 'cep',
    requiresToken: false,
    hasLocalFallback: false,
    fallbackDescription: 'Consulta de CEP indisponível',
  },
};

// ---------------------------------------------------------------------------
// URL base do Firebase hosting (segue o mesmo padrão de GoogleAPIFunctions)
// ---------------------------------------------------------------------------
const _getHost = () => {
  const { config } = firebase;
  const settings = store.getState()?.settingsdata?.settings;
  return (window && window.location && settings?.CompanyWebsite === window.location.origin)
    ? window.location.origin
    : `https://${config.projectId}.web.app`;
};

const _authHeader = () => {
  const { config } = firebase;
  return { "Authorization": "Basic " + base64.encode(config.projectId + ":" + AccessKey) };
};

// ---------------------------------------------------------------------------
// Controlo de quota
// ---------------------------------------------------------------------------
const _getUsageRef = (apiKey, limitType, userId) => {
  const { integrationUsageRef } = firebase;
  return limitType === 'per_user'
    ? integrationUsageRef(`${apiKey}/users/${userId}`)
    : integrationUsageRef(`${apiKey}/global`);
};

const _isLimitExceeded = async (apiKey, apiConfig, userId) => {
  if (!apiConfig.query_limit_enabled || !apiConfig.query_limit) return false;
  const ref = _getUsageRef(apiKey, apiConfig.limit_type, userId);
  const snap = await get(ref);
  return (snap.val() || 0) >= Number(apiConfig.query_limit);
};

const _incrementUsage = async (apiKey, apiConfig, userId) => {
  if (!apiConfig.query_limit_enabled) return;
  const ref = _getUsageRef(apiKey, apiConfig.limit_type, userId);
  await runTransaction(ref, (current) => (current || 0) + 1);
};

// ---------------------------------------------------------------------------
// CPF — proxy via Cloud Function /consulta_cpf  (evita CORS)
// Fallback: validador local isValidCPF quando API não configurada
// ---------------------------------------------------------------------------
export const consultarCPF = async (cpf, integrationConfig, userId = null) => {
  const config = integrationConfig?.cpf;
  const cleanCpf = String(cpf).replace(/\D/g, '');

  if (!config?.enabled || !config?.token) {
    return { valid: isValidCPF(cleanCpf), data: null, source: 'local' };
  }

  if (await _isLimitExceeded('cpf', config, userId)) {
    return { valid: isValidCPF(cleanCpf), data: null, source: 'local', limitExceeded: true };
  }

  try {
    const response = await fetch(`${_getHost()}/consulta_cpf`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ cpf: cleanCpf }),
    });
    const json = await response.json();
    if (json.success) {
      await _incrementUsage('cpf', config, userId);
      return { valid: true, data: json.data, source: 'api' };
    }
    return { valid: false, data: null, source: 'api', error: json.error || json.message };
  } catch (e) {
    return { valid: isValidCPF(cleanCpf), data: null, source: 'local', error: e.message };
  }
};

// Session-level cache: when the server confirms an API is not configured,
// skip all further requests for the rest of this JS runtime session.
const _notConfigured = new Set();

// ---------------------------------------------------------------------------
// CPF direto — sem depender de integrationConfig no cliente.
// Usado no cadastro mobile onde o Redux ainda não carregou a config.
// O servidor lê o token do Firebase por conta própria.
// ---------------------------------------------------------------------------
export const consultarCPFDireto = async (cpf) => {
  const cleanCpf = String(cpf).replace(/\D/g, '');
  if (cleanCpf.length !== 11) return { valid: false, data: null, source: 'local' };

  if (_notConfigured.has('cpf')) {
    return { valid: isValidCPF(cleanCpf), data: null, source: 'local' };
  }

  try {
    const url = `${_getHost()}/consulta_cpf`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ cpf: cleanCpf }),
    });
    const json = await response.json();

    if (!response.ok) {
      if (json.error === 'cpf_api_not_configured') {
        _notConfigured.add('cpf');
      }
      return { valid: isValidCPF(cleanCpf), data: null, source: 'local', error: json.error };
    }
    if (json.success) return { valid: true, data: json.data, source: 'api' };
    return { valid: isValidCPF(cleanCpf), data: null, source: 'local', error: json.error };
  } catch (e) {
    return { valid: isValidCPF(cleanCpf), data: null, source: 'local', error: e.message };
  }
};

// ---------------------------------------------------------------------------
// Placa — proxy via Cloud Function /consulta_placa  (evita CORS)
// Se integrationConfig não for passado (ex: mobile antes do Redux carregar),
// lê directamente do store — mesmo padrão usado por _getHost().
// ---------------------------------------------------------------------------
export const consultarPlaca = async (placa, integrationConfig, userId = null) => {
  const resolved = integrationConfig ?? store.getState()?.integrationdata?.integrationDetails;
  const config = resolved?.plates;
  const cleanPlaca = String(placa).replace(/[^A-Za-z0-9]/g, '').toUpperCase();

  if (!config?.enabled || !config?.token) return null;

  if (await _isLimitExceeded('plates', config, userId)) return null;

  try {
    const response = await fetch(`${_getHost()}/consulta_placa`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ placa: cleanPlaca }),
    });
    const json = await response.json();
    if (json.MARCA || json.marca) {
      await _incrementUsage('plates', config, userId);
      return json;
    }
    return null;
  } catch (e) {
    return null;
  }
};

// ---------------------------------------------------------------------------
// Placa direto — sem depender de integrationConfig no cliente.
// Usado no mobile onde o Redux ainda não carregou a config.
// O servidor lê o token do Firebase por conta própria.
// ---------------------------------------------------------------------------
export const consultarPlacaDireto = async (placa) => {
  const cleanPlaca = String(placa).replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  if (cleanPlaca.length < 7) return null;
  try {
    const response = await fetch(`${_getHost()}/consulta_placa`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ placa: cleanPlaca }),
    });
    if (!response.ok) return null;
    const json = await response.json();
    if (json.MARCA || json.marca) return json;
    return null;
  } catch (e) {
    return null;
  }
};

// ---------------------------------------------------------------------------
// CEP — ViaCEP é pública e suporta CORS, chamada directa ao browser
// ---------------------------------------------------------------------------
export const consultarCEP = async (cep, integrationConfig, userId = null) => {
  const config = integrationConfig?.cep;
  const cleanCep = String(cep).replace(/\D/g, '');

  if (!config?.enabled) return null;
  if (cleanCep.length !== 8) return null;

  if (await _isLimitExceeded('cep', config, userId)) return null;

  try {
    const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
    const json = await response.json();
    if (json.erro) return null;
    await _incrementUsage('cep', config, userId);
    return json;
  } catch (e) {
    return null;
  }
};
